import { useQuery } from "@apollo/client";
import { Add, Close, LocalOfferOutlined, Search, ShoppingCartOutlined, ViewInArOutlined } from "@mui/icons-material";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, MenuItem, Pagination, TextField, Typography, useMediaQuery,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { PRODUCTOS_CONFIGURACION_EVENTO } from "services/configuracionEventos";
import { formatoPesos, mensajeErrorEvento } from "../utils";

const POR_PAGINA = 12;
const campoSx = {
  minWidth: 0,
  "& .MuiOutlinedInput-root": { display: "flex !important", minHeight: 36, p: "0 11px !important", borderRadius: "9px !important", borderColor: "#CFCCE5", boxShadow: "0 1px 2px rgba(35,19,87,.04)" },
  "& .MuiInputBase-input": { flex: "1 1 0", minWidth: 0, width: "100% !important", boxSizing: "border-box", fontSize: "12px !important", fontFamily: "inherit", p: "7px 0 !important" },
  "& .MuiSelect-select": { pr: "24px !important" },
  "& .MuiOutlinedInput-root.Mui-focused": { borderColor: "#8456F7" },
  "& .MuiInputAdornment-root": { mr: 1, color: "#26145E", "& svg": { fontSize: 20 } },
  "& .MuiSelect-icon": { color: "#26145E" },
};
const botonSx = { borderRadius: "10px", minHeight: 40, px: 3, textTransform: "none", fontFamily: "inherit", fontSize: 14, fontWeight: 700 };

// Permite marcar varios productos y agregarlos de una vez. Los que ya están en
// el evento se muestran marcados y deshabilitados.
export default function SelectorProductosEvento({ open, categorias, idsExistentes, onCancel, onConfirm }) {
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [busqueda, setBusqueda] = useState("");
  const [busquedaAplicada, setBusquedaAplicada] = useState("");
  const [idCategoria, setIdCategoria] = useState("");
  const [pagina, setPagina] = useState(1);
  const [seleccion, setSeleccion] = useState([]);

  useEffect(() => {
    if (!open) return;
    setBusqueda(""); setBusquedaAplicada(""); setIdCategoria(""); setPagina(1); setSeleccion([]);
  }, [open]);

  useEffect(() => {
    const id = setTimeout(() => { setBusquedaAplicada(busqueda.trim()); setPagina(1); }, 300);
    return () => clearTimeout(id);
  }, [busqueda]);

  const filter = useMemo(() => ({
    esIngrediente: false,
    disponibilidad: true,
    ...(idCategoria ? { idCategoria: Number(idCategoria) } : {}),
  }), [idCategoria]);

  const { data, loading, error } = useQuery(PRODUCTOS_CONFIGURACION_EVENTO, {
    variables: { filter, search: busquedaAplicada || null, page: pagina, limit: POR_PAGINA },
    skip: !open,
    fetchPolicy: "network-only",
  });
  const productos = data?.productos || [];
  const paginas = Number(data?.paginationInfo?.pages || 0);
  const existentes = new Set(idsExistentes.map(String));
  const seleccionados = new Set(seleccion.map((producto) => String(producto.id)));

  const alternar = (producto) => setSeleccion((prev) => (
    seleccionados.has(String(producto.id))
      ? prev.filter((item) => String(item.id) !== String(producto.id))
      : [...prev, producto]
  ));

  return (
    <Dialog open={open} onClose={onCancel} fullScreen={fullScreen} fullWidth maxWidth={false}
      aria-labelledby="selector-productos-evento" aria-describedby="selector-productos-evento-ayuda"
      PaperProps={{ sx: {
        width: fullScreen ? "100%" : 1040, maxWidth: fullScreen ? "100%" : "calc(100% - 32px)",
        m: fullScreen ? 0 : 2, minHeight: { sm: "min(640px, calc(100% - 32px))" },
        maxHeight: fullScreen ? "100%" : "calc(100% - 32px)", borderRadius: fullScreen ? 0 : "14px",
        border: "1px solid #DDD5F3", background: "linear-gradient(130deg, #fff, #FDFCFF)",
        fontFamily: "Arial, sans-serif", color: "#191139",
        "& .MuiTypography-root": { fontFamily: "inherit", lineHeight: 1.4 },
      } }}>
      <DialogTitle component="div" sx={{ display: "flex", alignItems: "center", gap: 2, px: { xs: 2, sm: 2.75 }, py: 1.5, borderBottom: "1px solid #EEE8FC", background: "linear-gradient(110deg, #FAF8FF, #F5F2FC)" }}>
        <Box sx={{ display: { xs: "none", sm: "grid" }, placeItems: "center", width: 44, height: 44, flexShrink: 0, borderRadius: "12px", background: "#EAE2FF", color: "#3D16B4" }}><ViewInArOutlined sx={{ fontSize: 28 }} /></Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography id="selector-productos-evento" component="h2" sx={{ color: "#180F36", fontSize: { xs: 18, sm: 20 }, fontWeight: 700 }}>Agregar productos al evento</Typography>
          <Typography id="selector-productos-evento-ayuda" sx={{ color: "#514376", fontSize: 12.5, mt: 0.25 }}>
            Marque los productos que quiera vender y presione «Agregar». No se muestran ingredientes ni productos sin disponibilidad.
          </Typography>
        </Box>
        <IconButton aria-label="Cerrar selector de productos" onClick={onCancel} size="small" sx={{ alignSelf: "flex-start", mt: 0.75, width: 30, height: 30, border: "1px solid #D9CFF4", borderRadius: "9px", color: "#211343", background: "#FFFFFF99", "&:hover": { background: "#EEE7FE" } }}><Close sx={{ fontSize: 20 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2.25, gridTemplateColumns: "minmax(0, 1fr)", alignContent: "start", overflowX: "hidden", px: { xs: 2, sm: 2.75 }, py: "16px !important" }}>
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 2fr) minmax(0, 1fr)" } }}>
          <TextField size="small" placeholder="Buscar por nombre..." value={busqueda} sx={campoSx}
            onChange={(e) => setBusqueda(e.target.value)} inputProps={{ "aria-label": "Buscar producto" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
          <TextField size="small" select value={idCategoria} onChange={(e) => { setIdCategoria(e.target.value); setPagina(1); }}
            sx={campoSx} SelectProps={{ displayEmpty: true }} inputProps={{ "aria-label": "Categoría" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><LocalOfferOutlined /></InputAdornment> }}>
            <MenuItem value="">Todas las categorías</MenuItem>
            {categorias.map((categoria) => <MenuItem key={categoria.id} value={categoria.id}>{categoria.categoria}</MenuItem>)}
          </TextField>
        </Box>
        {error && <Alert severity="error">No se pudieron cargar los productos: {mensajeErrorEvento(error)}</Alert>}
        {loading ? <Box role="status" aria-label="Cargando productos" display="grid" sx={{ placeItems: "center", minHeight: 200 }}><CircularProgress size={28} sx={{ color: "#5D24D6" }} /></Box> : (
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" } }}>
            {productos.map((producto) => {
              const yaEsta = existentes.has(String(producto.id));
              const marcado = yaEsta || seleccionados.has(String(producto.id));
              return (
                <Box key={producto.id} component="label" sx={{
                  display: "flex !important", alignItems: "flex-start", gap: 1.25, px: 1.25, py: 1.1, minHeight: 90, minWidth: 0, borderRadius: "11px", cursor: yaEsta ? "default" : "pointer",
                  border: "1px solid", borderColor: marcado ? "#A477FF" : "#E1DCF3", background: marcado ? "linear-gradient(115deg, #F7F2FF, #F0E9FE)" : "linear-gradient(120deg, #fff, #FCFBFF)",
                  transition: "border-color .15s, background .15s", "&:hover": { borderColor: yaEsta ? "#A477FF" : "#AC8DE8" },
                  "&:focus-within": { outline: "2px solid #7A42D9", outlineOffset: 2 },
                }}>
                  <Checkbox checked={marcado} disabled={yaEsta} onChange={() => alternar(producto)}
                    sx={{ flexShrink: 0, width: 18, height: 18, p: 0, m: "1px 0 0", borderRadius: "5px", borderColor: "#C8C1DF", color: "#BEB4D7", "&.Mui-checked, &.Mui-checked.Mui-disabled": {
                      backgroundColor: "#5A20D4 !important", borderColor: "#5A20D4 !important", color: "#fff",
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 -1 22 22'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2.5' d='M6 10l3 3l6-6'/%3e%3c/svg%3e")`,
                    } }}
                    inputProps={{ "aria-label": `Seleccionar ${producto.nombre}` }} />
                  <Box minWidth={0} flex={1}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#191030" }} noWrap title={producto.nombre}>{producto.nombre}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#5F507D", mt: 0.2 }} noWrap title={producto.categoriaProducto?.categoria}>
                      {producto.categoriaProducto?.categoria || "Sin categoría"} · Carta
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mt: 0.65 }}>
                      <Typography component="span" sx={{ px: 1.25, py: 0.1, borderRadius: "10px", color: "#25134C", background: "#EEE7FC", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>{formatoPesos(producto.precioCarta)}</Typography>
                      {yaEsta && <Chip size="small" label="Ya agregado" sx={{ ml: "auto", height: 23, background: "#D5F2CF", color: "#28632A", fontSize: 11, fontWeight: 700, "& .MuiChip-label": { px: 1 } }} />}
                    </Box>
                  </Box>
                </Box>
              );
            })}
            {!productos.length && !error && <Typography role="status" sx={{ gridColumn: "1 / -1", textAlign: "center", py: 5, fontSize: 13, color: "#77698D" }}>No hay productos que coincidan.</Typography>}
          </Box>
        )}
        {paginas > 1 && <Box display="flex" justifyContent="center"><Pagination count={paginas} page={pagina} onChange={(_, value) => setPagina(value)} size="small"
          sx={{ "& .MuiPagination-ul": { flexWrap: "wrap", justifyContent: "center" }, "& .MuiPaginationItem-root": { fontFamily: "inherit", fontSize: 13, color: "#241143", mx: 0.25 }, "& .MuiPaginationItem-root.Mui-selected": { background: "#5721D5", color: "#fff", "&:hover": { background: "#4213B0" } } }} /></Box>}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 2.75 }, py: 1.75, borderTop: "1px solid #EEE8FC", gap: 1, flexWrap: "wrap", "& > :not(:first-of-type)": { ml: 0 } }}>
        <Box role="status" aria-live="polite" sx={{ display: "flex", alignItems: "center", gap: 1.5, mr: "auto", px: 1.75, py: 1, borderRadius: "10px", color: "#3E12A6", background: "#ECE4FF", width: { xs: "100%", sm: "auto" } }}>
          <ShoppingCartOutlined sx={{ fontSize: 22 }} /><Typography sx={{ fontSize: 14, fontWeight: 700 }}>{seleccion.length} seleccionado(s)</Typography>
        </Box>
        <Button variant="outlined" onClick={onCancel} sx={{ ...botonSx, flex: { xs: 1, sm: "none" }, color: "#321385", borderColor: "#A591D5", "&:hover": { borderColor: "#6A41B4", background: "#F6F1FF" } }}>Cancelar</Button>
        <Button variant="contained" startIcon={<Add />} disabled={!seleccion.length} onClick={() => onConfirm(seleccion)}
          sx={{ ...botonSx, flex: { xs: 1, sm: "none" }, background: "linear-gradient(110deg, #5821D1, #32109B)", color: "#fff", boxShadow: "0 3px 5px #3B16921A", "&:hover": { background: "#4215AD" }, "&.Mui-disabled": { background: "#E6DFF2", color: "#9D8DB6", boxShadow: "none" } }}>Agregar</Button>
      </DialogActions>
    </Dialog>
  );
}

SelectorProductosEvento.propTypes = {
  open: PropTypes.bool.isRequired,
  categorias: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), categoria: PropTypes.string })).isRequired,
  idsExistentes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};
